import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import StaffEditDrawer from './components/StaffEditDrawer';
import AddStaffModal from './components/AddStaffModal';
import { userService } from '../../../services/user.service';

const roleConfig: Record<string, { label: string; color: string }> = {
  admin: { label: 'מנהל מערכת', color: 'bg-rose-50 text-rose-500 border-rose-200' },
  coordinator: { label: 'רכז', color: 'bg-violet-50 text-violet-600 border-violet-200' },
};

function formatLastLogin(lastLogin: string | null): string {
  if (!lastLogin) return 'טרם התחבר';
  const date = new Date(lastLogin);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  const timeStr = date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `היום, ${timeStr}`;
  if (diffDays === 1) return `אתמול, ${timeStr}`;
  return `${date.toLocaleDateString('he-IL')}, ${timeStr}`;
}

export default function PermissionsPage() {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMember, setEditMember] = useState<any | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saved, setSaved] = useState(false);
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'coordinator'>('all');
  const [search, setSearch] = useState('');

  const loadStaff = () => {
    setLoading(true);
    userService.list().then((data) => { setStaff(data); setLoading(false); });
  };

  useEffect(() => { loadStaff(); }, []);

  const filtered = staff.filter((s) => {
    const matchRole = filterRole === 'all' || s.role === filterRole;
    const matchSearch = s.name.includes(search) || s.email.includes(search) || (s.center || '').includes(search);
    return matchRole && matchSearch;
  });

  const handleSave = async (data: any) => {
    if (!editMember) return;
    const updated = await userService.update(editMember.id, data);
    setStaff((prev) => prev.map((s) => (s.id === editMember.id ? updated : s)));
    setEditMember(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleAdd = async (data: any) => {
    const created = await userService.create(data);
    setStaff((prev) => [created, ...prev]);
    setShowAdd(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const activeCount = staff.filter((s) => s.staff_status === 'active').length;
  const adminCount = staff.filter((s) => s.role === 'admin').length;
  const coordCount = staff.filter((s) => s.role === 'coordinator').length;

  return (
    <AdminLayout title="הרשאות משתמשים" subtitle="ניהול עובדים וגישה למערכת">
      {saved && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-emerald-600 text-sm font-semibold">
          <i className="ri-checkbox-circle-line text-base"></i>
          השינויים נשמרו בהצלחה!
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'סה״כ משתמשים', value: staff.length, icon: 'ri-group-line', color: 'text-rose-500 bg-rose-50 border-rose-100' },
          { label: 'פעילים', value: activeCount, icon: 'ri-user-follow-line', color: 'text-emerald-500 bg-emerald-50 border-emerald-100' },
          { label: 'מנהלי מערכת', value: adminCount, icon: 'ri-shield-user-line', color: 'text-rose-500 bg-rose-50 border-rose-100' },
          { label: 'רכזים', value: coordCount, icon: 'ri-user-settings-line', color: 'text-violet-500 bg-violet-50 border-violet-100' },
        ].map((stat) => (
          <div key={stat.label} className={`flex items-center gap-3 px-4 py-3 bg-white rounded-xl border-2 ${stat.color.split(' ')[2]}`}>
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${stat.color.split(' ')[1]} ${stat.color.split(' ')[2]}`}>
              <i className={`${stat.icon} text-base ${stat.color.split(' ')[0]}`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800 leading-none">{stat.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="relative flex items-center">
          <div className="w-4 h-4 flex items-center justify-center absolute right-3 text-gray-400"><i className="ri-search-line text-sm"></i></div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש לפי שם, אימייל, מרכז..."
            className="pr-9 pl-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-white focus:outline-none focus:border-rose-300 w-60 transition-all" dir="rtl" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border-2 border-rose-100 rounded-xl p-1">
            {(['all', 'admin', 'coordinator'] as const).map((r) => (
              <button key={r} onClick={() => setFilterRole(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${filterRole === r ? 'bg-rose-400 text-white' : 'text-gray-500 hover:bg-rose-50'}`}>
                {r === 'all' ? 'הכל' : roleConfig[r].label}</button>
            ))}
          </div>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></div>
            הוסף משתמש
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-3">{filtered.length} משתמשים</p>

      {loading ? (
        <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-rose-100 overflow-hidden overflow-x-auto">
          <div className="grid grid-cols-[2fr_1.2fr_1.2fr_2fr_1fr_1.5fr_1.2fr_auto] gap-0 border-b-2 border-rose-100 bg-rose-50/60 px-4 py-3 min-w-[900px]">
            {['שם', 'תפקיד', 'מרכז', 'אימייל', 'סטטוס', 'כניסה אחרונה', 'סיסמה', ''].map((h) => (
              <span key={h} className="text-xs font-bold text-gray-500 px-2">{h}</span>
            ))}
          </div>
          {filtered.length > 0 ? (
            <div className="divide-y-2 divide-rose-50">
              {filtered.map((member) => {
                const role = roleConfig[member.role];
                const isActive = member.staff_status === 'active';
                return (
                  <div key={member.id} className="grid grid-cols-[2fr_1.2fr_1.2fr_2fr_1fr_1.5fr_1.2fr_auto] gap-0 px-4 py-3 items-center hover:bg-rose-50/30 transition-colors group min-w-[900px]">
                    <div className="flex items-center gap-2.5 px-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 font-bold text-xs flex-shrink-0">{member.name.charAt(0)}</div>
                      <p className="text-xs font-semibold text-gray-800 truncate">{member.name}</p>
                    </div>
                    <div className="px-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${role.color}`}>{role.label}</span></div>
                    <div className="px-2"><p className="text-xs text-gray-600 truncate">{member.center}</p></div>
                    <div className="px-2"><p className="text-xs text-gray-500 truncate">{member.email}</p></div>
                    <div className="px-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${isActive ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                        {isActive ? 'פעיל' : 'לא פעיל'}</span>
                    </div>
                    <div className="px-2"><p className="text-xs text-gray-500 whitespace-nowrap">{formatLastLogin(member.last_login_at)}</p></div>
                    <div className="px-2">
                      <button onClick={() => setEditMember(member)}
                        className="text-xs text-amber-500 font-semibold hover:text-amber-600 cursor-pointer whitespace-nowrap underline underline-offset-2">איפוס סיסמה</button>
                    </div>
                    <div className="px-2">
                      <button onClick={() => setEditMember(member)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-500 text-xs font-semibold hover:bg-rose-100 transition-all cursor-pointer whitespace-nowrap">
                        <i className="ri-edit-line text-xs"></i>עריכה</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-rose-50 border-2 border-rose-100 mb-3"><i className="ri-shield-user-line text-2xl text-rose-300"></i></div>
              <p className="text-gray-500 font-semibold text-sm">לא נמצאו משתמשים</p>
            </div>
          )}
        </div>
      )}

      {editMember && <StaffEditDrawer member={editMember} onClose={() => setEditMember(null)} onSave={handleSave} />}
      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onAdd={handleAdd} />}
    </AdminLayout>
  );
}
